import React from 'react';
import { Box, Stack, Typography, Button, useTheme, alpha, SxProps, Theme } from '@mui/material';

interface EmptyStateProps {
  /** Icon component to display (e.g. <GroupsOutlined />). Sized by this component. */
  icon: React.ReactElement;
  title: string;
  description?: React.ReactNode;
  /** Optional primary CTA. */
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactElement;
  };
  /** Optional secondary action — rendered as a text button next to the primary. */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Compact variant — smaller padding for inline use inside tabs or rails. */
  dense?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * One canonical empty-state look. Dashed border, tinted icon block,
 * centered text, optional primary + secondary actions. Use this instead
 * of writing per-page inline empty state blocks so density and styling
 * stay consistent across the portal.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon, title, description, action, secondaryAction, dense, sx
}) => {
  const theme = useTheme();
  const pad = dense ? { xs: 3 } : { xs: 4, sm: 5 };
  const iconSize = dense ? 32 : 40;
  const iconBox  = dense ? 48 : 56;

  return (
    <Box
      sx={{
        p: pad,
        borderRadius: 2,
        border: `1px dashed ${theme.palette.divider}`,
        textAlign: 'center',
        ...sx
      }}
    >
      <Stack alignItems="center" spacing={dense ? 1 : 1.5}>
        <Box
          sx={{
            width: iconBox, height: iconBox, borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: iconSize, ...((icon.props as any)?.sx || {}) } } as any)}
        </Box>

        <Box>
          <Typography variant={dense ? 'subtitle1' : 'h6'} fontWeight={700} color="text.primary">
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 460, mx: 'auto' }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {(action || secondaryAction) && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            {action && (
              <Button
                variant="contained"
                disableElevation
                startIcon={action.icon}
                onClick={action.onClick}
                sx={{ borderRadius: 2 }}
              >
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="text"
                color="inherit"
                onClick={secondaryAction.onClick}
                sx={{ color: 'text.secondary' }}
              >
                {secondaryAction.label}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default EmptyState;
